import { makeStyles } from "@material-ui/core/styles";
import { RefObject, useState } from "react";
import { changeAppState } from "../../../../../../../../common/appState";
import ShurikenProgress from "../../../../../../Animations/ShurikenProgress";
import { Markdown } from "../../../../../../Markdown";
import { User, UserForRanking } from "../../../../../../User/types";
import { useSyncCommentsStateWithBackgroundPanel } from "../../../hooks/useSyncCommentsStateWithBackgroundPanel";
import {
    ChangeCommenterAvatarElement,
    OriginKey,
    Post,
    PostComment,
} from "../../../types";
import { postLeftAvatarAreaWidth } from "../LeftAvatarArea";
import { CommentArea } from "./CommentArea";
import { LowerButtonArea } from "./LowerButtonArea";
import { UserNameAndTime } from "./UserNameAndTime";

export function RightContents({
    postedUser,
    post,
    userId,
    tmpMutedUserIds,
    setTmpMutedUserIds,
    isMyPost,
    deletePost,
    likePost,
    unlikePost,
    addCommentCount,
    user,
    isCommentShown,
    unreadCommentNos,
    isInputAreaHidden,
    hideLevelAndCountry,
    hideButtonTitle,
    changeCommenterAvatarElement,
    refreshUsersConnectionHeightList,
    hideTime,
    scrollableContainerRef,
    originKey,
}: {
    postedUser: UserForRanking;
    post: Post;
    userId: number;
    tmpMutedUserIds: number[];
    setTmpMutedUserIds: (ids: number[]) => void;
    isMyPost: boolean;
    deletePost: () => Promise<void>;
    likePost: () => Promise<void>;
    unlikePost: () => Promise<void>;
    addCommentCount: (addedCount: number) => void;
    user?: User;
    isCommentShown: boolean;
    unreadCommentNos: number[];
    isInputAreaHidden: boolean;
    hideLevelAndCountry?: boolean;
    hideButtonTitle?: boolean;
    changeCommenterAvatarElement: ChangeCommenterAvatarElement;
    refreshUsersConnectionHeightList: () => void;
    hideTime: boolean;
    scrollableContainerRef: RefObject<HTMLDivElement>;
    originKey: OriginKey;
}) {
    const c = useStyles();

    const [comments, setComments] = useState<PostComment[]>([]);
    useSyncCommentsStateWithBackgroundPanel(
        post,
        originKey,
        setComments,
        comments
    ); // Sync data with other panel

    const [commentedUsers, setCommentedUsers] = useState<UserForRanking[]>([]);
    const [showOnlyUnreadComment, setShowOnlyUnreadComment] = useState(
        unreadCommentNos.length > 0
    );
    const [areUnreadCommentsRemoved, setUnreadCommentsRemoved] =
        useState(false);

    const addTmpComment = (tmpComment: PostComment) => {
        setUnreadCommentsRemoved(true);
        setShowOnlyUnreadComment(false);
        if (user && !commentedUsers.some(u => u.userId === userId)) {
            setCommentedUsers([...commentedUsers, user]);
        }
        setComments([tmpComment, ...comments]);
    };

    return (
        <div className={c.container}>
            <UserNameAndTime
                postedUser={postedUser}
                minutesAgo={post.minutesAgo}
                hideLevelAndCountry={hideLevelAndCountry}
                hideTime={hideTime}
            />

            <div className={c.markdownContainer}>
                <PostedText text={post.text} />
            </div>

            <LowerButtonArea
                post={post}
                userId={userId}
                tmpMutedUserIds={tmpMutedUserIds}
                setTmpMutedUserIds={setTmpMutedUserIds}
                isMyPost={isMyPost}
                deletePost={deletePost}
                likePost={async () => {
                    if (!user) {
                        changeAppState("signInPanelState", { type: "signUp" });
                        return;
                    }
                    addTmpComment(getTmpLike(post, user.userId));
                    await likePost();
                }}
                unlikePost={unlikePost}
                hideButtonTitle={hideButtonTitle}
            />

            {isCommentShown && (
                <CommentArea
                    user={user}
                    addCommentCount={addCommentCount}
                    post={post}
                    tmpMutedUserIds={tmpMutedUserIds}
                    setTmpMutedUserIds={setTmpMutedUserIds}
                    unreadCommentNos={unreadCommentNos}
                    isInputAreaHidden={isInputAreaHidden}
                    changeCommenterAvatarElement={changeCommenterAvatarElement}
                    refreshUsersConnectionHeightList={
                        refreshUsersConnectionHeightList
                    }
                    comments={comments}
                    setComments={setComments}
                    commentedUsers={commentedUsers}
                    setCommentedUsers={setCommentedUsers}
                    showOnlyUnreadComment={showOnlyUnreadComment}
                    setShowOnlyUnreadComment={setShowOnlyUnreadComment}
                    areUnreadCommentsRemoved={areUnreadCommentsRemoved}
                    addTmpComment={addTmpComment}
                    scrollableContainerRef={scrollableContainerRef}
                    originKey={originKey}
                />
            )}
        </div>
    );
}
const useStyles = makeStyles(theme => ({
    container: { width: `calc(100% - ${postLeftAvatarAreaWidth}px)` },
    markdownContainer: {
        marginBottom: 5,
        paddingBottom: 10,
        paddingTop: 10,
        paddingRight: 5,
        borderBottom: "solid",
        borderBottomColor: theme.palette.grey[100],
        borderBottomWidth: 1,
        fontSize: 17,
    },
}));

function getTmpLike(post: Post, commentedUserId: number): PostComment {
    return {
        postedUserId: post.userId,
        postNo: post.postNo,
        commentNo: 0,
        commentedUserId,
        minutesAgo: 0,
        text: "",
        isLike: true,
        isRead: false,
        replyTargetUserId: null,
        replyTargetCommentNo: null,
        isReplyTargetRead: false,
        deleted: false,
    };
}

function PostedText({ text }: { text: string }) {
    if (!text) {
        return <ShurikenProgress size="7%" />;
    }
    if (/^Cleared the quest: \*\*Progress \d0%\*\* 🎉$/g.test(text)) {
        return (
            <div>
                {"Cleared the quest: "}
                <a
                    href="#"
                    onClick={ev => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        changeAppState("signInPanelState", {
                            type: "myPageTop",
                            initialView: "MyPageProgressPercentageCard",
                        });
                    }}
                    className="bold"
                >
                    {text
                        .replace("Cleared the quest: **", "")
                        .replace("** 🎉", "")}
                </a>
                {" 🎉"}
            </div>
        );
    }
    return (
        <Markdown
            source={text}
            noParagraphMarginBottom
            singleBreakLine
            isSocialPost
            isFontSmaller
        />
    );
}
